import {EulaRes} from '@/hooks/eula';
import {Locale} from '@epic-mw/i18n';

function doesLocaleIncludeRegion(locale: string) {
	return locale.length > 2;
}

function removeLocaleRegion(locale: string) {
	return locale.slice(0, 2);
}

const supportedEulaLocales: Locale[] = [
	'en', // English
	'ar', // Arabic
	'bg', // Bulgarian
	'cs', // Czech
	'da', // Danish
	'de', // German
	'es', // Latin America
	'es-ES', // Spanish
	'fi', // Finnish
	'fil', // Filipino
	'fr', // French
	'hi', // Hindi
	'hu', // Hungarian
	'id', // Indonesian
	'it', // Italian
	'ja', // Japanese
	'ko', // Korean
	'ms', // Malay
	'nl', // Dutch
	'no', // Norwegian
	'pl', // Polish
	'pt', // Portuguese
	'pt-BR', // Portuguese
	'ro', // Romanian
	'ru', // Russian
	'sv', // Swedish
	'th', // Thai
	'tr', // Turkish
	'uk', // Ukrainian
	'vi', // Vietnamese
	'zh-CN', // Simplified Chinese
	'zh-HK' // Traditional Chinese
];

/**
 * en-us || en_us || EN_US => en-US
 * EN => en
 */
const toLocaleCase = (locale: string) => {
	return locale
		.split(/-|_/)
		.map((part, i) => (i > 0 ? part.toUpperCase() : part.toLowerCase()))
		.join('-');
};

/**
 * EulaService is case sensitive and requires expected format
 * for all requests other than GET
 */
export const formatLocale = (rawLocale: string): Locale => {
	let locale = toLocaleCase(rawLocale);

	if (!supportedEulaLocales.includes(locale)) {
		if (doesLocaleIncludeRegion(locale)) {
			locale = removeLocaleRegion(locale);
		}
	}

	return locale;
};

interface Replacement {
	pattern: RegExp;
	replacement: string | Parameters<typeof String.prototype.replace>[1];
}

const eulaTitleTagsToHtml = (title: string) => {
	const replacements: Replacement[] = [];

	replacements.push(
		{
			pattern: /\n/gi,
			replacement: ''
		},
		{
			pattern: /<EULA\.HighlightItalic>([^<]*)<\/>/gi,
			replacement: '$1'
		},
		{
			pattern: /<EULA\.Header>([^<]*)<\/>/gi,
			replacement: '$1'
		},
		{
			pattern: /<EULA\.SubHeader>([^<]*)<\/>/gi,
			replacement: '$1'
		},
		{
			pattern: /<EULA\.Highlight>([^<]*)<\/>/gi,
			replacement: '$1'
		}
	);

	let revisedTitle = title;
	replacements.forEach((obj) => {
		//eslint-disable-next-line @typescript-eslint/no-unsafe-argument
		revisedTitle = revisedTitle.replace(obj.pattern, obj.replacement as any);
	});

	return revisedTitle;
};

const eulaContentTagsToHtml = (content: string) => {
	const replacements: Replacement[] = [];

	replacements.push(
		{
			pattern: /\n/gi,
			replacement: '<br />'
		},
		{
			pattern: /<a\b([^>]*)>(.*?)<\/a>|<a\b([^>]*)>(.*?)<\/>/gi,
			replacement: (
				match: string,
				attrs1: string,
				content1: string,
				attrs2: string,
				content2: string
			) => {
				const attrs = attrs1 || attrs2;
				const content = content1 || content2;

				const hasTarget = /target\s*=\s*(['"])[^'"]*\1/i.test(attrs);
				const hasRel = /rel\s*=\s*(['"])[^'"]*\1/i.test(attrs);

				let newAttrs = attrs;

				if (!hasTarget) {
					newAttrs = `target="_blank" ${newAttrs}`;
				}

				if (!hasRel) {
					newAttrs = `rel="noopener noreferrer" ${newAttrs}`;
				}

				newAttrs = newAttrs.trim().replace(/\s+/g, ' ');

				return `<a ${newAttrs}>${content}</a>`;
			}
		},
		{
			pattern: /<EULA\.HighlightItalic>([^<]*)<\/>/gi,
			replacement: '<strong><em class="highlight-italic">$1</em></strong>'
		},
		{
			pattern: /<EULA\.Header>([^<]*)<\/>/gi,
			replacement: '<strong class="header">$1</strong>'
		},
		{
			pattern: /<EULA\.SubHeader>([^<]*)<\/>/gi,
			replacement: '<strong class="subheader"><em>$1</em></strong>'
		},
		{
			pattern: /<EULA\.Highlight>([^<]*)<\/>/gi,
			replacement: '<strong class="highlight"><em>$1</em></strong>'
		}
	);

	let revisedContent = content;
	replacements.forEach((obj) => {
		//eslint-disable-next-line @typescript-eslint/no-unsafe-argument
		revisedContent = revisedContent.replace(obj.pattern, obj.replacement as any);
	});

	return revisedContent;
};

export const formatEula = (eulaResponse: EulaRes & {description?: string}): EulaRes => {
	let convertedEula = eulaResponse;

	if (eulaResponse) {
		convertedEula = Object.assign({}, eulaResponse);
		if (convertedEula.title) {
			convertedEula.title = eulaTitleTagsToHtml(convertedEula.title);
		}
		if (convertedEula.body) {
			convertedEula.body = eulaContentTagsToHtml(convertedEula.body);
		}
		if (convertedEula.description) {
			convertedEula.description = eulaTitleTagsToHtml(convertedEula.description);
		}
	}

	return convertedEula;
};
